import { RenderingOptions } from '../../model/dataset/rendering/rendering-options';
import { VisualizationType } from '../../model/dataset/rendering/visualization-options';
import { ColorScaleType } from '../../model/dataset/rendering/color-scale-type';
import { RenderingErrorCodes } from '../../model/dataset/rendering/rendering-error-codes';
import { IntervalOption } from '../../model/dataset/rendering/interval-option';

export class RenderingUtils {

    /**
     * This method will return a key for the first invalid data.
     */
    static validate(renderingOptions: RenderingOptions): string | null {
        switch (renderingOptions.visualizationOptions.type) {
            case VisualizationType.DEFAULT:
                return RenderingUtils.validateBasicRendering(renderingOptions);
            case VisualizationType.CLUSTER:
                return RenderingUtils.validateBasicRendering(renderingOptions);
            case VisualizationType.THEMATIC_MAP:
                return RenderingUtils.validateThematicMapRendering(renderingOptions);
            case VisualizationType.HEATMAP:
                break;


        }
        return null;
    }


    private static validateBasicRendering(renderingOptions: RenderingOptions): string | null {
        switch (renderingOptions.datasetStylingOptions.type) {
            case ColorScaleType.CONSTANT:
                if (!renderingOptions.datasetStylingOptions.defaultColor) {
                    return RenderingErrorCodes.DEFAULT_COLOR;
                }
                break;
            case ColorScaleType.FIXED:
            case ColorScaleType.GRADIENT:
                if (!renderingOptions.datasetStylingOptions.colors || !renderingOptions.datasetStylingOptions.colors.length) {
                    return RenderingErrorCodes.COLORS;
                } // must have at least one color
                if (!renderingOptions.converterOptions.fieldID) {
                    return RenderingErrorCodes.FIELD;
                }
                break;
            case ColorScaleType.INTERVAL:
                if (!renderingOptions.datasetStylingOptions.defaultColor) {
                    return RenderingErrorCodes.DEFAULT_COLOR;
                }
                if (!renderingOptions.converterOptions.fieldID) {
                    return RenderingErrorCodes.FIELD;
                }
                if (!renderingOptions.datasetStylingOptions.intervalOptions.length) {
                    return RenderingErrorCodes.INTERVALS;
                }
                return this.validateIntervalsOverlapping(renderingOptions);
        }

        return null;
    }

    private static validateIntervalsOverlapping(renderingOptions: RenderingOptions): string | null {
        const sourceIntervals = renderingOptions.datasetStylingOptions.intervalOptions;

        // Remove any point intervals [n, n] from the source array before sorting/validating
        for (let i = sourceIntervals.length - 1; i >= 0; i--) {
            const interval = sourceIntervals[i];
            if (interval.minValue != null && interval.maxValue != null &&
                Number(interval.minValue) === Number(interval.maxValue)) {
                sourceIntervals.splice(i, 1);
                return RenderingErrorCodes.INTERVALS_OVERLAP;
            }
        }

        let sortedIntervalOptions: IntervalOption[] = sourceIntervals.sort(this.getCompareIntervalsFunction());

        // Check for overlaps between adjacent intervals
        for (let i = 1; i <= sortedIntervalOptions.length - 1; i++) {
            const currentIntervalMax = sortedIntervalOptions[i].maxValue;
            const currentIntervalMin = sortedIntervalOptions[i].minValue;
            const previousIntervalMax = sortedIntervalOptions[i - 1].maxValue;
            const previousIntervalMin = sortedIntervalOptions[i - 1].minValue;

            // Only the last interval can have no upper bound
            if (currentIntervalMax == null && i !== sortedIntervalOptions.length - 1) {
                return RenderingErrorCodes.INTERVALS_OVERLAP;
            }

            // All intervals except the first must have a minValue
            if (currentIntervalMin == null) {
                return RenderingErrorCodes.INTERVALS_OVERLAP;
            }

            // Convert to numbers for proper numeric comparison (handles string values)
            const currentMin = Number(currentIntervalMin);
            const currentMax = currentIntervalMax != null ? Number(currentIntervalMax) : null;
            const prevMax = previousIntervalMax != null ? Number(previousIntervalMax) : null;
            const prevMin = Number(previousIntervalMin);

            // Adjacent intervals like [0,1) and [1,2) are valid (exclusive upper bound)
            if (prevMax !== null && currentMin < prevMax) {
                return RenderingErrorCodes.INTERVALS_OVERLAP;
            }

            // Check for duplicate intervals (same min and max values)
            if (currentMin === prevMin && currentMax !== null && prevMax !== null && currentMax === prevMax) {
                return RenderingErrorCodes.DUPLICATE_INTERVAL;
            }
        }
        return null;
    }

    private static getCompareIntervalsFunction(): (a: IntervalOption, b: IntervalOption) => number {
        return (a: IntervalOption, b: IntervalOption) => {
            // Handle null/undefined minValues - push to end
            if ((a.minValue == null && a.minValue !== 0) || (b.minValue == null && b.minValue !== 0)) {
                if (a.minValue == null && a.minValue !== 0) return 1;
                if (b.minValue == null && b.minValue !== 0) return -1;
            }

            
            // Convert to numbers for proper comparison
            const aMin = Number(a.minValue);
            const bMin = Number(b.minValue);
            
            // Sort by minValue first
            if (aMin !== bMin) {
                return aMin - bMin;
            }
            
            // If minValues are equal, sort by maxValue
            const aMax = a.maxValue != null ? Number(a.maxValue) : Infinity;
            const bMax = b.maxValue != null ? Number(b.maxValue) : Infinity;
            return aMax - bMax;
        };
    }

    private static validateThematicMapRendering(renderingOptions: RenderingOptions): string | null {
        if (!renderingOptions.datasetStylingOptions.defaultColor) {
            return RenderingErrorCodes.DEFAULT_COLOR;
        }
        if (!renderingOptions.converterOptions.accountApplicationFieldId) {
            return RenderingErrorCodes.APPLICATION_FIELD;
        }
        if (!renderingOptions.datasetStylingOptions.intervalOptions.length) {
            return RenderingErrorCodes.INTERVALS;
        }
        return this.validateIntervalsOverlapping(renderingOptions);
    }
}
